import { useRef, useState } from 'react';
import {
  IconCheckCircleFilled,
  IconCopyOutline,
  IconEmailOutline,
} from '@iwoca/icons';
import { Button } from '@iwoca/orion';
import { useAmplitude } from 'hooks/useAmplitude';
import { TOfferDataRequirement } from 'lendingApi/lapiOverrides.types';
import { useGetSignupState } from 'lendingApi/lendingApiHooks';
import CopyToClipboard from 'react-copy-to-clipboard';
import { AMPLITUDE_EVENTS } from 'utils/tracking/tracking.events';
import {
  requirementsTitle,
  generateCopyStringBody,
  generateBodyTextString,
} from '../helpers/ShareRequirements.helper';
import styles from './ShareRequirements.module.css';

type TProps = {
  requiredDocs: TOfferDataRequirement['actions'];
  optionalDocs: TOfferDataRequirement['actions'];
};

export const ShareRequirements = ({ requiredDocs, optionalDocs }: TProps) => {
  const { state } = useGetSignupState();
  const [isCopyClicked, setIsCopyClicked] = useState(false);
  const requirementListRef = useRef<HTMLDivElement>(null);
  const { trackEvent } = useAmplitude();

  const renderedDocumentRequirements = () => {
    const requiredDocsText = requiredDocs.map((requirement, index) => {
      return (
        <li key={index}>
          <span className={styles.title}>
            {requirementsTitle(requirement.type, requirement.quantifier?.value)}
          </span>
        </li>
      );
    });

    const optionalDocsText = optionalDocs.map((requirement, index) => {
      return (
        <li key={index}>
          {requirementsTitle(requirement.type, requirement.quantifier?.value)}{' '}
          (optional)
        </li>
      );
    });

    return (
      <>
        {requiredDocsText && <ul>{requiredDocsText}</ul>}
        {optionalDocsText && <ul>{optionalDocsText}</ul>}
      </>
    );
  };

  const clipboardText = () => {
    let textToCopy: string = '';
    if (requiredDocs && optionalDocs) {
      textToCopy = generateCopyStringBody({
        requiredDocs,
        optionalDocs,
      });
    }

    textToCopy = textToCopy + `\nFiles must be PDF and no more than 2GB`;

    return textToCopy;
  };

  const generateMailToLink = () => {
    if (requiredDocs && optionalDocs) {
      const mailtoBody = `${generateBodyTextString({
        docRequirements: requiredDocs,
        newLineBreak: '%0D%0A',
      })}${generateBodyTextString({
        docRequirements: optionalDocs,
        newLineBreak: '%0D%0A',
      })}`;

      const applicant = state?.application?.people?.find((person) =>
        person.roles?.includes('applicant'),
      );

      const firstName = applicant?.first_name;
      const firstNameSpaced = firstName ? ` ${firstName}` : '';
      return `mailto:?body=Hi${firstNameSpaced},%0D%0A%0D%0AAs discussed, I’m applying for a loan with iwoca on behalf of your business. To verify your trading history I need to provide several documents. %0D%0A%0D%0APlease can you send me: %0D%0A%0D%0A${mailtoBody} %0D%0A%0D%0APlease ensure files clearly show the business name, are PDF and no more than 2GB. %0D%0A%0D%0AIncluding optional documents may increase your chance of approval. &subject=Help with documents for new loan`;
    }
  };

  return (
    <div className={styles.container}>
      <p className={styles.subTitle}>
        If you don’t have the right things to hand, you can email (or copy and
        paste) this list to someone who does.
      </p>
      <div className={styles.requirementsList} ref={requirementListRef}>
        {renderedDocumentRequirements()}
        <p className={styles.description}>
          Files must be PDF and no more than 2GB
        </p>
      </div>
      <div className={styles.copyButton}>
        <CopyToClipboard
          text={clipboardText()}
          onCopy={() => setIsCopyClicked(true)}
        >
          <Button
            variant="tertiary"
            className={styles.copyButtonInner}
            onClick={() =>
              trackEvent(AMPLITUDE_EVENTS.copyRequirementsListClicked)
            }
          >
            Copy this list as text
            <IconCopyOutline />
          </Button>
        </CopyToClipboard>
        {isCopyClicked && (
          <div className={styles.copyButtonSuccess}>
            <IconCheckCircleFilled className={styles.copyCheckIcon} />
            <p className={styles.copyText}>List copied</p>
          </div>
        )}
      </div>
      <div>
        <Button
          variant="tertiary"
          href={generateMailToLink()}
          onClick={() =>
            trackEvent(AMPLITUDE_EVENTS.emailRequirementsListClicked)
          }
          className={styles.emailButton}
          target="_blank"
          rel="noreferrer"
        >
          Email this list to someone
          <IconEmailOutline />
        </Button>
      </div>
    </div>
  );
};
